import React from 'react'
import {
  FormControl,
  Select,
  OutlinedInput,
  Box,
  Chip,
  MenuItem,
  Checkbox,
  ListItemText,
  FormHelperText,
  SxProps,
  InputLabel,
  MenuItemProps,
  TypographyProps
} from '@mui/material'
import {
  FieldValues,
  useController,
  UseControllerProps
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface ListType extends Omit<MenuItemProps, 'id'> {
  id: string | number
  name: string
  disabled?: boolean
  textProps?: TypographyProps
}

interface Props<FormValues extends FieldValues>
  extends UseControllerProps<FormValues> {
  itemList: ListType[]
  sx?: SxProps
  required?: boolean
  helperText?: string
  label?: string
  hideSelectAll?: boolean
}
const ListMultiSelect = <FormValues extends FieldValues>({
  itemList,
  sx,
  required,
  helperText,
  label,
  hideSelectAll,
  ...controllerProps
}: Props<FormValues>) => {
  const { t } = useTranslation()
  const availableItemsList = itemList.filter((itemList) => !itemList.disabled)
  const {
    field,
    fieldState: { error }
  } = useController({
    rules: {
      ...(required && {
        required: {
          value: true,
          message: t('validations.required')
        }
      })
    },
    ...controllerProps
  })

  const isAllSelected =
    availableItemsList.length > 0 &&
    availableItemsList.length === field.value?.length

  return (
    <FormControl
      sx={{
        width: 300,
        ...sx
      }}
      error={!!error}
    >
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        id={field.name}
        multiple
        value={field.value ?? []}
        onChange={(e) => {
          const value = e.target.value as any
          const lastSelected = value[value.length - 1]
          if (lastSelected === 'SELECT_ALL_OPTIONS' && isAllSelected) {
            return field.onChange([] as any)
          }
          if (lastSelected === 'SELECT_ALL_OPTIONS') {
            return field.onChange(
              availableItemsList.map((item) => item.id) as any
            )
          }
          field.onChange(value)
        }}
        input={<OutlinedInput id={field.name} label={label} />}
        renderValue={(selected: string[]) => (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5
            }}
          >
            {isAllSelected && !hideSelectAll ? (
              <Chip label={t('labels.All-f')} />
            ) : (
              selected.map((value) => (
                <Chip
                  key={value}
                  label={itemList.find((channel) => channel.id === value)?.name}
                />
              ))
            )}
          </Box>
        )}
      >
        {!hideSelectAll && (
          <MenuItem
            value={'SELECT_ALL_OPTIONS'}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            <Checkbox checked={isAllSelected} />
            <ListItemText primary={t('labels.All-f')} />
          </MenuItem>
        )}
        {itemList.map(({ id, name, textProps, ...menuProps }) => (
          <MenuItem key={id} value={id} {...menuProps}>
            <Checkbox checked={field.value?.indexOf(id) > -1} />
            <ListItemText primary={name} primaryTypographyProps={textProps} />
          </MenuItem>
        ))}
      </Select>
      {(error?.message || helperText) && (
        <FormHelperText>{error?.message || helperText}</FormHelperText>
      )}
    </FormControl>
  )
}
export default ListMultiSelect
